import type { QueryKey } from "@tanstack/react-query";
import type {
  ClassificationAnalyticsParams,
  ClassificationSummaryParams,
} from "@/domain/classification";
import type { DashboardMetricsParams } from "@/domain/dashboard";
import type { NotificationFilterState } from "@/domain";
import { isObject as isRecord } from "./cache/cacheSlots";
import { sortTerms, type Filters, type ListView, type SortTerm } from "./cache/listViews";

function listViewAt(
  key: QueryKey,
  segment: string,
  defaultSort: readonly SortTerm[],
): ListView | null {
  const query = key[3];
  if (key[2] !== segment || !isRecord(query) || !isRecord(query.filters)) return null;
  const sort = sortTerms(query.sort);
  return { filters: query.filters, sort: sort.length > 0 ? sort : defaultSort };
}

const CREATED_DESC: readonly SortTerm[] = [{ key: "created_at", direction: "desc" }];
const NAME_ASC: readonly SortTerm[] = [{ key: "name", direction: "asc" }];
const TASK_DEFAULT_SORT: readonly SortTerm[] = [
  { key: "urgency", direction: "desc" },
  { key: "due_at", direction: "asc" },
];
const QUOTA_DEFAULT_SORT: readonly SortTerm[] = [
  { key: "money.estimate.available", direction: "desc" },
];

function filtersAt(key: QueryKey, segment: string): Filters | null {
  return key[2] === segment && isRecord(key[3]) ? key[3] : null;
}

export const queryKeys = {
  comments: {
    all: (orgId: string) => ["comments", orgId] as const,
    lists: (orgId: string) => ["comments", orgId, "list"] as const,
    details: (orgId: string) => ["comments", orgId, "detail"] as const,
    summaries: (orgId: string) => ["comments", orgId, "summary"] as const,
    list: (orgId: string, scope: object) => ["comments", orgId, "list", scope] as const,
    parseList: (key: QueryKey): ListView | null =>
      key[2] === "list" && isRecord(key[3])
        ? { filters: { ...key[3], status: key[3].status ?? "open" }, sort: [] }
        : null,
    parseSummary: (key: QueryKey) => filtersAt(key, "summary"),
    detail: (orgId: string, uuid: string) => ["comments", orgId, "detail", uuid] as const,
    summary: (orgId: string, scope: object) => ["comments", orgId, "summary", scope] as const,
    people: (orgId: string) => ["comments", orgId, "people"] as const,
  },
  activity: {
    all: (orgId: string) => ["activity", orgId] as const,
    list: (orgId: string, filters: object) => ["activity", orgId, "list", filters] as const,
    aggregates: (orgId: string, filters: object) =>
      ["activity", orgId, "aggregates", filters] as const,
    export: (orgId: string, filters: object) => ["activity", orgId, "export", filters] as const,
  },
  classifications: {
    all: (orgId: string) => ["classifications", orgId] as const,
    lists: (orgId: string) => ["classifications", orgId, "list"] as const,
    details: (orgId: string) => ["classifications", orgId, "detail"] as const,
    summaries: (orgId: string) => ["classifications", orgId, "summary"] as const,
    analyticsAll: (orgId: string) => ["classifications", orgId, "analytics"] as const,
    countsAll: (orgId: string) => ["classifications", orgId, "counts"] as const,
    list: (orgId: string, beneficiaryUuid: string, params?: Record<string, unknown>) =>
      ["classifications", orgId, "list", beneficiaryUuid, params] as const,
    detail: (orgId: string, beneficiaryUuid: string, uuid: string) =>
      ["classifications", orgId, "detail", beneficiaryUuid, uuid] as const,
    counterpart: (orgId: string, beneficiaryUuid: string, uuid: string) =>
      ["classifications", orgId, "detail", beneficiaryUuid, uuid, "counterpart"] as const,
    summary: (orgId: string, beneficiaryUuid: string, params?: ClassificationSummaryParams) =>
      ["classifications", orgId, "summary", beneficiaryUuid, params] as const,
    parseList: (key: QueryKey): ListView | null => {
      const params = key[4];
      if (key[2] !== "list" || typeof key[3] !== "string" || !isRecord(params)) return null;
      const { sort_by, sort_direction, pageSize: _pageSize, ...filters } = params;
      return {
        filters: { ...filters, beneficiary: key[3] },
        sort: sortTerms([
          typeof sort_by === "string"
            ? { key: sort_by, direction: sort_direction ?? "desc" }
            : { key: "created_at", direction: "desc" },
        ]),
      };
    },
    parseSummary: (key: QueryKey): Filters | null => {
      const params = key[4];
      if (key[2] !== "summary" || typeof key[3] !== "string") return null;
      return {
        beneficiary: key[3],
        fiscal_quota_uuid: isRecord(params) ? params.fiscal_quota_uuid : undefined,
      };
    },
    suppliersAnalytics: (
      orgId: string,
      beneficiaryUuid: string,
      params?: ClassificationAnalyticsParams,
    ) => ["classifications", orgId, "analytics", beneficiaryUuid, "suppliers", params] as const,
    taxBreakdown: (
      orgId: string,
      beneficiaryUuid: string,
      params?: ClassificationAnalyticsParams,
    ) => ["classifications", orgId, "analytics", beneficiaryUuid, "tax-breakdown", params] as const,
    workforce: (orgId: string, beneficiaryUuid: string, params?: ClassificationAnalyticsParams) =>
      ["classifications", orgId, "analytics", beneficiaryUuid, "workforce", params] as const,
    counts: (orgId: string, beneficiaryUuid: string, params?: Record<string, unknown>) =>
      ["classifications", orgId, "counts", beneficiaryUuid, params] as const,
    activity: (orgId: string, beneficiaryUuid: string) =>
      ["classifications", orgId, "activity", beneficiaryUuid] as const,
  },
  admin: {
    all: () => ["admin"] as const,
    dashboard: () => ["admin", "dashboard"] as const,
    organizationsAll: () => ["admin", "organizations"] as const,
    organizations: (params?: Record<string, unknown>) =>
      ["admin", "organizations", params] as const,
    organizationDetail: (uuid: string) => ["admin", "organizations", "detail", uuid] as const,
    organizationMembers: (uuid: string) => ["admin", "organizations", "members", uuid] as const,
    organizationInvitations: (uuid: string) =>
      ["admin", "organizations", "invitations", uuid] as const,
    organizationUsage: (uuid: string, from?: string, to?: string) =>
      ["admin", "organizations", "usage", uuid, from ?? null, to ?? null] as const,
    organizationUsageAll: (uuid: string) => ["admin", "organizations", "usage", uuid] as const,
    organizationEntitlements: (uuid: string) =>
      ["admin", "organizations", "entitlements", uuid] as const,
    usersAll: () => ["admin", "users"] as const,
    users: (params?: Record<string, unknown>) => ["admin", "users", params] as const,
    userDetail: (uuid: string) => ["admin", "users", "detail", uuid] as const,
    userMembershipsAll: () => ["admin", "users", "memberships"] as const,
    userMemberships: (uuid: string) => ["admin", "users", "memberships", uuid] as const,
  },
  investments: {
    all: (orgId: string) => ["investments", orgId] as const,
    lists: (orgId: string) => ["investments", orgId, "list"] as const,
    kanbanColumns: (orgId: string) => ["investments", orgId, "kanban-column"] as const,
    details: (orgId: string) => ["investments", orgId, "detail"] as const,
    documentsAll: (orgId: string) => ["investments", orgId, "documents"] as const,
    aggregatesAll: (orgId: string) => ["investments", orgId, "aggregates"] as const,
    list: (
      orgId: string,
      filters?: Record<string, unknown>,
      pagination?: Record<string, unknown>,
      viewMode?: string,
    ) => ["investments", orgId, "list", filters, pagination, viewMode] as const,
    kanbanColumn: (orgId: string, states: string[], filters?: Record<string, unknown>) =>
      ["investments", orgId, "kanban-column", states.join(","), filters] as const,
    parseList: (key: QueryKey) => listViewAt(key, "list", CREATED_DESC),
    parseKanbanColumn: (key: QueryKey): ListView | null => {
      const query = key[4];
      if (key[2] !== "kanban-column" || typeof key[3] !== "string" || !isRecord(query)) return null;
      const { sort, ...filters } = query;
      const terms = sortTerms(sort);
      return {
        filters: { ...filters, state: key[3].split(",") },
        sort: terms.length > 0 ? terms : CREATED_DESC,
      };
    },
    parseAggregates: (key: QueryKey) => filtersAt(key, "aggregates"),
    aggregates: (
      orgId: string,
      filters?: Record<string, unknown>,
      aggregates?: readonly string[],
    ) => ["investments", orgId, "aggregates", filters, aggregates] as const,
    detail: (orgId: string, uuid: string) => ["investments", orgId, "detail", uuid] as const,
    documents: (orgId: string, uuid: string) => ["investments", orgId, "documents", uuid] as const,
  },
  dashboard: {
    all: (orgId: string) => ["dashboard", orgId] as const,
    metrics: (orgId: string, filters?: DashboardMetricsParams) =>
      ["dashboard", orgId, "metrics", filters] as const,
  },
  tasks: {
    all: (orgId: string) => ["tasks", orgId] as const,
    lists: (orgId: string) => ["tasks", orgId, "list"] as const,
    aggregatesAll: (orgId: string) => ["tasks", orgId, "aggregates"] as const,
    list: (
      orgId: string,
      filters?: Record<string, unknown>,
      pagination?: Record<string, unknown>,
    ) => ["tasks", orgId, "list", filters, pagination] as const,
    aggregates: (
      orgId: string,
      filters?: Record<string, unknown>,
      aggregates?: readonly string[],
    ) => ["tasks", orgId, "aggregates", filters, aggregates] as const,
    summary: (orgId: string) => ["tasks", orgId, "summary"] as const,
    parseList: (key: QueryKey) => listViewAt(key, "list", TASK_DEFAULT_SORT),
    parseAggregates: (key: QueryKey) => filtersAt(key, "aggregates"),
    parseSummary: (key: QueryKey): Filters | null => (key[2] === "summary" ? {} : null),
  },
  investmentEvents: {
    infinite: (orgId: string) => ["investmentEvents", orgId, "infinite"] as const,
    all: (orgId: string) => ["investmentEvents", orgId] as const,
    list: (orgId: string, limit: number) => ["investmentEvents", orgId, "list", limit] as const,
  },
  pipeline: {
    all: (orgId: string) => ["pipeline", orgId] as const,
    query: (
      orgId: string,
      params?: {
        beneficiary_uuid?: string;
        fiscal_year?: number;
        fiscal_quota_uuid?: string;
      },
    ) => ["pipeline", orgId, "query", params] as const,
    metricsAll: (orgId: string) => ["pipeline", orgId, "metrics"] as const,
    metrics: (orgId: string, filters: Filters) => ["pipeline", orgId, "metrics", filters] as const,
    parseMetrics: (key: QueryKey) => filtersAt(key, "metrics"),
  },
  templates: {
    all: (orgId: string) => ["templates", orgId] as const,
    lists: (orgId: string) => ["templates", orgId, "list"] as const,
    details: (orgId: string) => ["templates", orgId, "detail"] as const,
    list: (orgId: string, params?: Record<string, unknown>) =>
      ["templates", orgId, "list", params] as const,
    parseList: (key: QueryKey) => listViewAt(key, "list", NAME_ASC),
    detail: (orgId: string, uuid: string) => ["templates", orgId, "detail", uuid] as const,
  },
  placeholders: {
    all: (orgId: string) => ["placeholders", orgId] as const,
    catalog: (orgId: string) => ["placeholders", orgId, "catalog"] as const,
  },
  proposals: {
    lists: (orgId: string) => ["proposals", orgId, "list"] as const,
    details: (orgId: string) => ["proposals", orgId, "detail"] as const,
    publicAll: () => ["proposals", "public"] as const,
    list: (orgId: string, params?: Record<string, unknown>) =>
      ["proposals", orgId, "list", params] as const,
    detail: (orgId: string, uuid: string) => ["proposals", orgId, "detail", uuid] as const,
    publicOf: (uuid: string) => ["proposals", "public", uuid] as const,
    public: (uuid: string, token: string) => ["proposals", "public", uuid, token] as const,
  },
  hub: {
    all: ["hub"] as const,
    profile: () => ["hub", "profile"] as const,
    profiles: () => ["hub", "profile", "all"] as const,
    dashboard: () => ["hub", "dashboard"] as const,
    pendingActionsAll: () => ["hub", "pendingActions"] as const,
    pendingActions: (params?: Record<string, unknown>) =>
      ["hub", "pendingActions", params] as const,
    investments: (params: Record<string, unknown>) => ["hub", "investments", params] as const,
    investmentDetail: (uuid: string) => ["hub", "investmentDetail", uuid] as const,
    investmentDocuments: (investmentUuid: string) =>
      ["hub", "investmentDocuments", investmentUuid] as const,
  },
  contracts: {
    all: (orgId: string) => ["contracts", orgId] as const,
    lists: (orgId: string) => ["contracts", orgId, "list"] as const,
    details: (orgId: string) => ["contracts", orgId, "detail"] as const,
    aggregatesAll: (orgId: string) => ["contracts", orgId, "aggregates"] as const,
    list: (
      orgId: string,
      filters?: Record<string, unknown>,
      pagination?: Record<string, unknown>,
    ) => ["contracts", orgId, "list", filters, pagination] as const,
    aggregates: (
      orgId: string,
      filters?: Record<string, unknown>,
      aggregates?: readonly string[],
    ) => ["contracts", orgId, "aggregates", filters, aggregates] as const,
    parseList: (key: QueryKey) => listViewAt(key, "list", CREATED_DESC),
    parseAggregates: (key: QueryKey) => filtersAt(key, "aggregates"),
    detail: (orgId: string, uuid: string) => ["contracts", orgId, "detail", uuid] as const,
  },
  notifications: {
    all: () => ["notifications"] as const,
    lists: () => ["notifications", "list"] as const,
    list: (params?: NotificationFilterState) => ["notifications", "list", params] as const,
    stats: () => ["notifications", "stats"] as const,
    parseList: (key: QueryKey): ListView | null =>
      key[1] === "list"
        ? {
            filters: isRecord(key[2]) ? key[2] : {},
            sort: [{ key: "created_at", direction: "desc" }],
          }
        : null,
    parseStats: (key: QueryKey): Filters | null => (key[1] === "stats" ? {} : null),
  },
  beneficiaries: {
    all: (orgId: string) => ["beneficiaries", orgId] as const,
    lists: (orgId: string) => ["beneficiaries", orgId, "list"] as const,
    details: (orgId: string) => ["beneficiaries", orgId, "detail"] as const,
    documentsAll: (orgId: string) => ["beneficiaries", orgId, "documents"] as const,
    documentsByQuotaAll: (orgId: string) => ["beneficiaries", orgId, "documents-by-quota"] as const,
    aggregatesAll: (orgId: string) => ["beneficiaries", orgId, "aggregates"] as const,
    metricsAll: (orgId: string) => ["beneficiaries", orgId, "metrics"] as const,
    quotaLists: (orgId: string) => ["beneficiaries", orgId, "quota-list"] as const,
    fiscalQuotaLists: (orgId: string) => ["beneficiaries", orgId, "fiscal-quotas"] as const,
    quotas: (orgId: string) => ["beneficiaries", orgId, "quota"] as const,
    quotaAggregatesAll: (orgId: string) => ["beneficiaries", orgId, "quota-aggregates"] as const,
    fiscalYearSummaries: (orgId: string) =>
      ["beneficiaries", orgId, "fiscal-year-summary"] as const,
    consolidationPreviews: (orgId: string) =>
      ["beneficiaries", orgId, "fiscal-quota-consolidation-preview"] as const,
    quotaReportsAll: (orgId: string) => ["beneficiaries", orgId, "fiscal-quota-reports"] as const,
    list: (orgId: string, params?: Record<string, unknown>) =>
      ["beneficiaries", orgId, "list", params] as const,
    aggregates: (orgId: string, filters: Record<string, unknown>, aggregates: readonly string[]) =>
      ["beneficiaries", orgId, "aggregates", filters, [...aggregates].sort()] as const,
    parseList: (key: QueryKey) => listViewAt(key, "list", NAME_ASC),
    parseAggregates: (key: QueryKey) => filtersAt(key, "aggregates"),
    detail: (orgId: string, id: string) => ["beneficiaries", orgId, "detail", id] as const,
    documents: (orgId: string, uuid: string, params?: Record<string, unknown>) =>
      params !== undefined
        ? (["beneficiaries", orgId, "documents", uuid, params] as const)
        : (["beneficiaries", orgId, "documents", uuid] as const),
    documentsByQuota: (orgId: string, uuid: string, params?: Record<string, unknown>) =>
      params !== undefined
        ? (["beneficiaries", orgId, "documents-by-quota", uuid, params] as const)
        : (["beneficiaries", orgId, "documents-by-quota", uuid] as const),
    metrics: (orgId: string, params?: Record<string, unknown>) =>
      ["beneficiaries", orgId, "metrics", params] as const,
    fiscalQuotasByBeneficiary: (orgId: string, beneficiaryUuid: string) =>
      ["beneficiaries", orgId, "fiscal-quotas", beneficiaryUuid] as const,
    quotaList: (orgId: string, query: Record<string, unknown>, page: Record<string, unknown>) =>
      ["beneficiaries", orgId, "quota-list", query, page] as const,
    parseQuotaList: (key: QueryKey) => listViewAt(key, "quota-list", QUOTA_DEFAULT_SORT),
    parseQuotaAggregates: (key: QueryKey) => filtersAt(key, "quota-aggregates"),
    quotaAggregates: (
      orgId: string,
      filters: Record<string, unknown>,
      aggregates: readonly string[],
    ) => ["beneficiaries", orgId, "quota-aggregates", filters, [...aggregates].sort()] as const,
    quota: (orgId: string, quotaUuid: string) =>
      ["beneficiaries", orgId, "quota", quotaUuid] as const,
    fiscalYearSummary: (orgId: string, beneficiaryUuid: string, year: number) =>
      ["beneficiaries", orgId, "fiscal-year-summary", beneficiaryUuid, year] as const,
    fiscalQuotaConsolidationPreview: (orgId: string, beneficiaryUuid: string, quotaUuid: string) =>
      [
        "beneficiaries",
        orgId,
        "fiscal-quota-consolidation-preview",
        beneficiaryUuid,
        quotaUuid,
      ] as const,
    fiscalQuotaReports: (orgId: string, quotaUuid: string) =>
      ["beneficiaries", orgId, "fiscal-quota-reports", quotaUuid] as const,
  },
  investorDropdown: {
    all: (orgId: string) => ["investorDropdown", orgId] as const,
    search: (orgId: string, search: string) => ["investorDropdown", orgId, search] as const,
  },
  beneficiaryDropdown: {
    all: (orgId: string) => ["beneficiaryDropdown", orgId] as const,
    search: (orgId: string, search: string) => ["beneficiaryDropdown", orgId, search] as const,
  },
  templateDropdown: {
    all: (orgId: string) => ["templateDropdown", orgId] as const,
    search: (orgId: string, search: string, filters: Record<string, string | undefined>) =>
      ["templateDropdown", orgId, search, filters] as const,
  },
  investors: {
    all: (orgId: string) => ["investors", orgId] as const,
    lists: (orgId: string) => ["investors", orgId, "list"] as const,
    records: (orgId: string) => ["investors", orgId, "record"] as const,
    documentsAll: (orgId: string) => ["investors", orgId, "documents"] as const,
    aggregatesAll: (orgId: string) => ["investors", orgId, "aggregates"] as const,
    investmentsAll: (orgId: string) => ["investors", orgId, "investments"] as const,
    investmentMetricsAll: (orgId: string) => ["investors", orgId, "investmentMetrics"] as const,
    metricsAll: (orgId: string) => ["investors", orgId, "metrics"] as const,
    dashboardMetricsAll: (orgId: string) => ["investors", orgId, "dashboardMetrics"] as const,
    list: (orgId: string, params?: Record<string, unknown>) =>
      ["investors", orgId, "list", params] as const,
    aggregates: (orgId: string, filters: Record<string, unknown>, aggregates: readonly string[]) =>
      ["investors", orgId, "aggregates", filters, [...aggregates].sort()] as const,
    parseList: (key: QueryKey) => listViewAt(key, "list", NAME_ASC),
    parseAggregates: (key: QueryKey) => filtersAt(key, "aggregates"),
    record: (orgId: string, uuid: string) => ["investors", orgId, "record", uuid] as const,
    investments: (orgId: string, uuid: string, filters?: Record<string, unknown>) =>
      ["investors", orgId, "investments", uuid, filters] as const,
    investmentMetrics: (orgId: string, uuid: string, fiscalYear?: number) =>
      ["investors", orgId, "investmentMetrics", uuid, fiscalYear] as const,
    documents: (orgId: string, uuid: string) => ["investors", orgId, "documents", uuid] as const,
    metrics: (orgId: string, params?: Record<string, unknown>) =>
      ["investors", orgId, "metrics", params] as const,
    dashboardMetrics: (orgId: string, params?: DashboardMetricsParams) =>
      ["investors", orgId, "dashboardMetrics", params] as const,
  },
  projects: {
    all: (orgId: string) => ["projects", orgId] as const,
    details: (orgId: string) => ["projects", orgId, "detail"] as const,
    beneficiaryProjectsAll: (orgId: string) => ["projects", orgId, "beneficiaryProjects"] as const,
    detail: (orgId: string, projectUuid: string) =>
      ["projects", orgId, "detail", projectUuid] as const,
    beneficiaryProjectsList: (
      orgId: string,
      beneficiaryUuid: string,
      params?: Record<string, unknown>,
    ) =>
      params !== undefined
        ? (["projects", orgId, "beneficiaryProjects", beneficiaryUuid, "list", params] as const)
        : (["projects", orgId, "beneficiaryProjects", beneficiaryUuid, "list"] as const),
    parseBeneficiaryProjectsList: (key: QueryKey): ListView | null => {
      const params = key[5];
      if (key[4] !== "list" || !isRecord(params) || (Number(params.offset) || 0) > 0) return null;
      const {
        sort_by,
        sort_direction,
        limit: _limit,
        offset: _offset,
        page: _page,
        ...filters
      } = params;
      return {
        filters,
        sort: sortTerms([
          {
            key: typeof sort_by === "string" ? sort_by : "title",
            direction: sort_direction ?? "asc",
          },
        ]),
      };
    },
  },
  notes: {
    all: (orgId: string) => ["notes", orgId] as const,
    lists: (orgId: string) => ["notes", orgId, "list"] as const,
    details: (orgId: string) => ["notes", orgId, "detail"] as const,
    aggregatesAll: (orgId: string) => ["notes", orgId, "aggregates"] as const,
    list: (orgId: string, params: Record<string, unknown>) =>
      ["notes", orgId, "list", params] as const,
    aggregates: (orgId: string, scope: Record<string, unknown>) =>
      ["notes", orgId, "aggregates", scope] as const,
    detail: (orgId: string, uuid: string) => ["notes", orgId, "detail", uuid] as const,
  },
  tags: {
    all: (orgId: string) => ["tags", orgId] as const,
    list: (orgId: string, params?: Record<string, unknown>) =>
      ["tags", orgId, "list", params] as const,
  },
  userProfile: {
    all: () => ["userProfile"] as const,
    current: () => ["userProfile", "current"] as const,
    preferences: () => ["userProfile", "preferences"] as const,
  },
  invitations: {
    all: () => ["invitations"] as const,
    verify: (invitationUuid: string, token: string) =>
      ["invitations", "verify", invitationUuid, token] as const,
  },
  modules: {
    all: (orgId: string) => ["modules", orgId] as const,
    entitlements: (orgId: string) => ["modules", orgId, "entitlements"] as const,
  },
  /** Signed URLs expire on their own schedule: kept out of every entity prefix so no mutation refetches them. */
  signed: {
    classificationFile: (orgId: string, beneficiaryUuid: string, uuid: string) =>
      ["signed", orgId, "classification-file", beneficiaryUuid, uuid] as const,
    contractDocument: (orgId: string, documentUuid: string) =>
      ["signed", orgId, "contract-document", documentUuid] as const,
    templateImage: (orgId: string, templateUuid: string, src: string) =>
      ["signed", orgId, "template-image", templateUuid, src] as const,
    projectPosters: (orgId: string, projectUuid: string) =>
      ["signed", orgId, "project-posters", projectUuid] as const,
    projectPoster: (orgId: string, projectUuid: string) =>
      ["signed", orgId, "project-poster", projectUuid] as const,
  },
  organization: {
    all: (orgId: string) => ["organization", orgId] as const,
    userOrganizations: () => ["organization", "userOrganizations"] as const,
    details: (orgId: string) => ["organization", orgId, "details"] as const,
    users: (orgId: string) => ["organization", orgId, "users"] as const,
    invitations: (orgId: string) => ["organization", orgId, "invitations"] as const,
    templates: (orgId: string) => ["organization", orgId, "templates"] as const,
  },
};

/**
 * Top-level query key prefixes that are NOT scoped to an organization.
 * Used by the org-switch flow to preserve these caches across switches.
 *
 * When adding a new top-level key in `queryKeys`, ask: does it depend on
 * the current org? If no, add the prefix here.
 */
export const NON_ORG_SCOPED_QUERY_KEYS = [
  "admin",
  "hub",
  "userProfile",
  "notifications",
  "invitations",
  "organization",
] as const;
